import { useCallback, useEffect, useRef, useState } from 'react';
import type { VoiceParticipant, VoiceSignalPayload, VoiceSignalRelayPayload } from '@big2/shared';
import { trackEvent } from '../analytics.js';
import { processVoice, type ProcessedVoice } from './voiceProcessing.js';

export interface VoiceChatController {
  voiceJoined: boolean;
  voiceMuted: boolean;
  voiceConnecting: boolean;
  isSpeaking: boolean;
  voiceError: string | null;
  isVip: boolean;
  audioMuted: boolean;
  toggleAudioMute: () => void;
  joinVoice: () => Promise<void>;
  leaveVoice: () => void;
  toggleVoiceMute: () => void;
}

interface UseVoiceChatOptions {
  roomId: string | null;
  playerId: string | null;
  voiceEnabled: boolean;
  participants: VoiceParticipant[];
  isVip?: boolean;
  setVoiceState: (joined: boolean, muted: boolean) => void;
  sendVoiceSignal: (payload: VoiceSignalPayload) => void;
  subscribeVoiceSignal: (listener: (payload: VoiceSignalRelayPayload) => void) => () => void;
}

const RTC_CONFIG: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun.cloudflare.com:3478' },
  ],
};

export function useVoiceChat({
  roomId,
  playerId,
  voiceEnabled,
  participants,
  isVip = false,
  setVoiceState,
  sendVoiceSignal,
  subscribeVoiceSignal,
}: UseVoiceChatOptions): VoiceChatController {
  const [voiceJoined, setVoiceJoined] = useState(false);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [voiceConnecting, setVoiceConnecting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [audioMuted, setAudioMuted] = useState(false);

  const localStreamRef = useRef<MediaStream | null>(null);
  const processedVoiceRef = useRef<ProcessedVoice | null>(null);
  const peersRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const pendingCandidatesRef = useRef<Map<string, RTCIceCandidateInit[]>>(new Map());
  const voiceJoinedRef = useRef(false);
  const voiceMutedRef = useRef(false);
  const audioMutedRef = useRef(false);
  const joinRequestRef = useRef(0);
  const joinedRoomRef = useRef<string | null>(null);
  const voiceEnabledRef = useRef(voiceEnabled);
  const playerIdRef = useRef<string | null>(playerId);
  const roomIdRef = useRef<string | null>(roomId);

  voiceJoinedRef.current = voiceJoined;
  voiceMutedRef.current = voiceMuted;
  audioMutedRef.current = audioMuted;
  playerIdRef.current = playerId;
  roomIdRef.current = roomId;
  voiceEnabledRef.current = voiceEnabled;

  const toggleAudioMute = useCallback(() => {
    setAudioMuted((prev) => {
      const next = !prev;
      audioMutedRef.current = next;
      for (const el of audioElementsRef.current.values()) {
        try {
          el.muted = next;
        } catch {
          // Ignore DOM audio errors
        }
      }
      return next;
    });
  }, []);

  const closePeer = useCallback((peerId: string) => {
    const pc = peersRef.current.get(peerId);
    if (pc) {
      try {
        pc.onicecandidate = null;
        pc.ontrack = null;
        pc.onconnectionstatechange = null;
        pc.close();
      } catch {
        // Ignore close errors
      }
      peersRef.current.delete(peerId);
    }
    pendingCandidatesRef.current.delete(peerId);
    const audioEl = audioElementsRef.current.get(peerId);
    if (audioEl) {
      try {
        audioEl.pause();
        audioEl.srcObject = null;
        audioEl.remove();
      } catch {
        // Ignore DOM audio cleanup errors
      }
      audioElementsRef.current.delete(peerId);
    }
  }, []);

  const cleanupAllMedia = useCallback(() => {
    joinRequestRef.current += 1;
    joinedRoomRef.current = null;
    setIsSpeaking(false);
    for (const peerId of [...peersRef.current.keys()]) {
      closePeer(peerId);
    }
    peersRef.current.clear();
    pendingCandidatesRef.current.clear();
    if (processedVoiceRef.current) {
      try {
        processedVoiceRef.current.stop();
      } catch {
        // Ignore media track stop errors
      }
      processedVoiceRef.current = null;
    }
    localStreamRef.current = null;
  }, [closePeer]);

  const ensurePeerConnection = useCallback(
    (peerId: string): RTCPeerConnection | null => {
      if (typeof RTCPeerConnection === 'undefined') return null;
      const existing = peersRef.current.get(peerId);
      if (existing) return existing;

      try {
        const pc = new RTCPeerConnection(RTC_CONFIG);
        peersRef.current.set(peerId, pc);

        if (localStreamRef.current) {
          for (const track of localStreamRef.current.getAudioTracks()) {
            pc.addTrack(track, localStreamRef.current);
          }
        }

        pc.onicecandidate = (ev) => {
          if (ev.candidate && ev.candidate.candidate) {
            sendVoiceSignal({
              targetPlayerId: peerId,
              type: 'ice',
              candidate: {
                candidate: ev.candidate.candidate,
                sdpMid: ev.candidate.sdpMid ?? null,
                sdpMLineIndex: ev.candidate.sdpMLineIndex ?? null,
                usernameFragment: ev.candidate.usernameFragment ?? null,
              },
            });
          }
        };

        pc.ontrack = (ev) => {
          const remoteStream = ev.streams?.[0];
          if (!remoteStream || typeof document === 'undefined') return;
          let audioEl = audioElementsRef.current.get(peerId);
          if (!audioEl) {
            audioEl = document.createElement('audio');
            audioEl.autoplay = true;
            audioEl.setAttribute('playsinline', 'true');
            audioEl.style.display = 'none';
            document.body.appendChild(audioEl);
            audioElementsRef.current.set(peerId, audioEl);
          }
          audioEl.srcObject = remoteStream;
          audioEl.muted = audioMutedRef.current;
          void audioEl.play().catch(() => {
            // Autoplay is permitted because joining voice requires a user gesture
          });
        };

        pc.onconnectionstatechange = () => {
          if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
            closePeer(peerId);
          }
        };

        return pc;
      } catch {
        return null;
      }
    },
    [closePeer, sendVoiceSignal],
  );

  const flushPendingCandidates = useCallback(async (peerId: string, pc: RTCPeerConnection) => {
    const queued = pendingCandidatesRef.current.get(peerId);
    if (!queued || queued.length === 0) return;
    pendingCandidatesRef.current.delete(peerId);
    for (const cand of queued) {
      try {
        await pc.addIceCandidate(cand);
      } catch {
        // Ignore stale ICE candidates
      }
    }
  }, []);

  // Listen for incoming WebRTC signaling messages
  useEffect(() => {
    return subscribeVoiceSignal(async (signal: VoiceSignalRelayPayload) => {
      if (!voiceEnabledRef.current) return;
      const peerId = signal.fromPlayerId;
      if (!peerId || peerId === playerIdRef.current) return;

      try {
        if (signal.type === 'offer' && signal.sdp) {
          const pc = ensurePeerConnection(peerId);
          if (!pc) return;
          await pc.setRemoteDescription({ type: 'offer', sdp: signal.sdp.sdp });
          await flushPendingCandidates(peerId, pc);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          if (answer.sdp) {
            sendVoiceSignal({
              targetPlayerId: peerId,
              type: 'answer',
              sdp: { type: 'answer', sdp: answer.sdp },
            });
          }
        } else if (signal.type === 'answer' && signal.sdp) {
          const pc = peersRef.current.get(peerId);
          if (!pc) return;
          await pc.setRemoteDescription({ type: 'answer', sdp: signal.sdp.sdp });
          await flushPendingCandidates(peerId, pc);
        } else if (signal.type === 'ice' && signal.candidate) {
          const pc = peersRef.current.get(peerId);
          const candInit: RTCIceCandidateInit = {
            candidate: signal.candidate.candidate,
            sdpMid: signal.candidate.sdpMid ?? null,
            sdpMLineIndex: signal.candidate.sdpMLineIndex ?? null,
            usernameFragment: signal.candidate.usernameFragment ?? undefined,
          };
          if (pc && pc.remoteDescription) {
            await pc.addIceCandidate(candInit);
          } else {
            const list = pendingCandidatesRef.current.get(peerId) ?? [];
            list.push(candInit);
            pendingCandidatesRef.current.set(peerId, list);
          }
        }
      } catch {
        // Fault isolation: never allow WebRTC signaling errors to surface to game UI
      }
    });
  }, [ensurePeerConnection, flushPendingCandidates, sendVoiceSignal, subscribeVoiceSignal]);

  // Synchronize peer connections whenever room participants change
  useEffect(() => {
    if (joinedRoomRef.current && roomId !== joinedRoomRef.current) {
      cleanupAllMedia();
      setVoiceJoined(false);
      setVoiceMuted(false);
      setVoiceConnecting(false);
      return;
    }
    if (!roomId || !voiceEnabled) {
      if (voiceJoinedRef.current || voiceConnecting) {
        cleanupAllMedia();
        setVoiceJoined(false);
        setVoiceMuted(false);
        setVoiceConnecting(false);
      }
      return;
    }

    if (!playerId) return;

    const activePeerIds = new Set(
      participants.filter((p) => p.playerId !== playerId).map((p) => p.playerId),
    );

    // Close connections for peers who left voice chat
    for (const existingPeerId of [...peersRef.current.keys()]) {
      if (!activePeerIds.has(existingPeerId)) {
        closePeer(existingPeerId);
      }
    }

    // Listener mode: connect receive-only to active speakers in room
    if (!voiceJoined) {
      for (const peerId of activePeerIds) {
        if (!peersRef.current.has(peerId)) {
          const pc = ensurePeerConnection(peerId);
          if (pc) {
            void (async () => {
              try {
                if (typeof pc.addTransceiver === 'function') {
                  pc.addTransceiver('audio', { direction: 'recvonly' });
                }
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);
                if (offer.sdp) {
                  sendVoiceSignal({
                    targetPlayerId: peerId,
                    type: 'offer',
                    sdp: { type: 'offer', sdp: offer.sdp },
                  });
                }
              } catch {
                // Ignore offer creation failure
              }
            })();
          }
        }
      }
      return;
    }

    // Speaker mode: connect to other speakers (lexicographically smaller initiates)
    for (const peerId of activePeerIds) {
      if (!peersRef.current.has(peerId)) {
        const pc = ensurePeerConnection(peerId);
        if (pc && playerId.localeCompare(peerId) < 0) {
          void (async () => {
            try {
              const offer = await pc.createOffer();
              await pc.setLocalDescription(offer);
              if (offer.sdp) {
                sendVoiceSignal({
                  targetPlayerId: peerId,
                  type: 'offer',
                  sdp: { type: 'offer', sdp: offer.sdp },
                });
              }
            } catch {
              // Ignore offer creation failure
            }
          })();
        }
      }
    }
  }, [roomId, playerId, voiceEnabled, voiceJoined, voiceConnecting, participants, cleanupAllMedia, closePeer, ensurePeerConnection, sendVoiceSignal]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupAllMedia();
    };
  }, [cleanupAllMedia]);

  const joinVoice = useCallback(async () => {
    if (!isVip) {
      setVoiceError(
        typeof window !== 'undefined' && window.location.pathname.includes('/mn')
          ? 'Зөвхөн VIP гишүүд микрофон ашиглах эрхтэй.'
          : 'Only VIP members can use the microphone.'
      );
      return;
    }
    if (!roomIdRef.current || !voiceEnabled || voiceJoinedRef.current || voiceConnecting) return;
    const requestId = ++joinRequestRef.current;
    const joiningRoom = roomIdRef.current;
    joinedRoomRef.current = joiningRoom;
    setVoiceError(null);
    setVoiceConnecting(true);

    try {
      if (typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia) {
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
            video: false,
          });
        } catch {
          // Fallback to basic audio constraints if advanced audio processing options are unsupported
          stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
          });
        }
        if (requestId !== joinRequestRef.current || joiningRoom !== roomIdRef.current || !voiceEnabledRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        const processed = processVoice(stream, {
          onSpeakingChange: (speaking) => {
            setIsSpeaking(speaking);
          },
        });
        processedVoiceRef.current = processed;
        localStreamRef.current = processed.stream;
      } else {
        throw new Error('MIC_UNAVAILABLE');
      }

      setVoiceJoined(true);
      setVoiceMuted(false);
      setVoiceConnecting(false);
      setVoiceState(true, false);
      trackEvent('voice_chat_joined', { room_id: roomIdRef.current });
    } catch {
      if (requestId !== joinRequestRef.current) return;
      cleanupAllMedia();
      setVoiceConnecting(false);
      setVoiceJoined(false);
      setIsSpeaking(false);
      setVoiceError('mic_denied');
      trackEvent('voice_chat_error', { reason: 'mic_denied', room_id: roomIdRef.current });
    }
  }, [cleanupAllMedia, setVoiceState, voiceConnecting, voiceEnabled]);

  const leaveVoice = useCallback(() => {
    cleanupAllMedia();
    setVoiceJoined(false);
    setVoiceMuted(false);
    setIsSpeaking(false);
    setVoiceConnecting(false);
    setVoiceError(null);
    if (roomIdRef.current) {
      setVoiceState(false, false);
      trackEvent('voice_chat_left', { room_id: roomIdRef.current });
    }
  }, [cleanupAllMedia, setVoiceState]);

  const toggleVoiceMute = useCallback(() => {
    if (!voiceJoinedRef.current) return;
    const nextMuted = !voiceMutedRef.current;
    processedVoiceRef.current?.setMuted(nextMuted);
    if (nextMuted) {
      setIsSpeaking(false);
    }
    setVoiceMuted(nextMuted);
    setVoiceState(true, nextMuted);
    trackEvent('voice_chat_mute_toggled', { muted: nextMuted, room_id: roomIdRef.current });
  }, [setVoiceState]);

  return {
    voiceJoined,
    voiceMuted,
    voiceConnecting,
    isSpeaking,
    voiceError,
    isVip,
    audioMuted,
    toggleAudioMute,
    joinVoice,
    leaveVoice,
    toggleVoiceMute,
  };
}
